/**
 * Overview contracts — slice 10, "the Overview screen".
 *
 * This file is mostly an argument about what a dashboard is allowed to say, and
 * only incidentally a set of shapes.
 *
 * **Every number here changes a decision.** Contact counts, template counts,
 * segment counts and total-campaigns-ever are absent, and their absence is the
 * design. They are true, they are one cheap `count(*)` each, and no reader has
 * ever done anything differently because of one. Filling the page is the named
 * failure mode of this screen: a grid of eight tiles where two matter teaches
 * people to stop reading all eight, and the two that matter here are a tenant's
 * own AWS account being put under review and their WhatsApp token expiring.
 *
 * **The order of the KPIs is a decision, not a preference.** Bounce rate and
 * complaint rate lead because under bring-your-own-SES the account AWS reviews
 * is the *customer's*, the suspension lands on the customer, and Wend's first
 * job is that the warning does not come from Amazon. Deliverability follows,
 * because slices 5 and 6 kept `accepted` and `delivered` apart precisely so
 * this screen could show the gap. Then whether this workspace can send at all —
 * a sandboxed account reaching nobody is the most common "the product is
 * broken" report there is. Then credits, then the suppression trend, then the
 * last few campaigns.
 *
 * **Nothing in *this object* is measured by opening or clicking, and slice 11
 * did not change that.** There is no open rate, no click rate and no WhatsApp
 * read count here — not as a number, not as a zero, not as an em dash, not as a
 * greyed-out tile. The reason has narrowed but has not gone away: a tile reading
 * "0% opened" when the truth is "this tenant has tracking off" is a lie the
 * reader has no way to detect, and it is a *worse* lie now that the tile is
 * genuinely capable of showing a real zero.
 *
 * Slice 11 built the measurement, and it lives in `./engagement.js` behind
 * `engagementPanelSchema` — a union whose untracked arm has **no field a number
 * can live in** — read through its own endpoint rather than spliced in here.
 * That separation is this file's other rule doing its job: VDM's per-ISP panel
 * is filled by a live `BatchGetMetricData` call on the tenant's own AWS
 * account, and drawing this dashboard must not spend a tenant's provider rate
 * limit or fail because AWS is slow. WhatsApp read receipts remain slice 12 and
 * remain absent. A schema is the right place to enforce all of it, because a
 * field that is not in a `.strict()` object cannot be rendered by accident.
 *
 * **Every provider fact on this screen comes from the stored `last_check`
 * snapshot, and none of it comes from a live call.** Drawing a dashboard must
 * not spend a tenant's AWS or Meta rate limit, and must not fail because Meta
 * is slow. The cost of that is staleness, which is paid honestly:
 * {@link overviewSnapshotSchema} carries when the check ran and
 * {@link snapshotStaleness} says how old that is, so the card says "checked 6
 * days ago" instead of implying it is current.
 *
 * **Rates are refused below a volume floor rather than shown small.** See
 * {@link OVERVIEW_MIN_VOLUME_FOR_RATE} and {@link rateReading}: below the floor
 * the rate is literally `null` on the wire, so the only thing a component can
 * render is the raw counts and the reason.
 *
 * What this file does **not** own: the AWS thresholds and the five
 * not-delivered outcomes both live in `./delivery.js` and are imported. They
 * are the SES vocabulary, `delivery.ts` documents them, and a second copy here
 * would be a second number to keep in step with AWS.
 */
import { z } from 'zod';

import { isoDateTimeSchema, uuidSchema } from '../common.js';
import {
  campaignStatusSchema,
  channelSchema,
  featureSchema,
  suppressionReasonSchema,
  type Channel,
  type Feature,
} from '../enums.js';
import { CREDITS_PER_MESSAGE, availableCredits, type CreditAmounts } from './credit.js';
import { deliveryReportsSchema } from './delivery-reports.js';
import { projectedFiguresSchema } from './projection.js';
import {
  AWS_BOUNCE_RATE_REVIEW,
  AWS_COMPLAINT_RATE_REVIEW,
  bounceBreakdownSchema,
  notDeliveredBreakdownSchema,
} from './delivery.js';
import {
  metaReadinessStateSchema,
  metaTokenExpiryStateSchema,
  sesReadinessStateSchema,
  type SesQuota,
} from './integration.js';

/* -------------------------------------------------------------------------- */
/* Thresholds — the number that makes a percentage mean something              */
/* -------------------------------------------------------------------------- */

/**
 * Which AWS review threshold belongs to which headline rate.
 *
 * A percentage is not a KPI until the reader knows which side of it is bad, so
 * the threshold travels with the rate rather than being remembered by whoever
 * writes the tile. Both values are AWS's own published review points, imported
 * from `./delivery.js` rather than restated: 5% of messages bouncing, or 0.1%
 * of them being reported as spam, is where AWS starts looking at an account.
 *
 * **What crossing one costs is not a Wend problem, and that is the point.**
 * Under BYO the credentials are the tenant's, the sending account is the
 * tenant's, and AWS's response — review, then probation, then a sending pause
 * that stops every email the tenant sends from that account, Wend's or not —
 * lands on the tenant. Wend cannot appeal it and cannot see it coming from
 * inside AWS. The only useful thing this product can do is show the number
 * early enough to act on, next to the line it must not cross.
 *
 * These remain approximations of AWS's figure. AWS computes reputation over a
 * whole account across a rolling window that includes mail Wend never sent;
 * this is Wend's own traffic over {@link OVERVIEW_ACTIVITY_WINDOW_DAYS}. It is
 * an early warning, and a screen that presents it as the number in the SES
 * console is making a promise this product cannot keep.
 */
export const OVERVIEW_RATE_THRESHOLDS = {
  bounce: AWS_BOUNCE_RATE_REVIEW,
  complaint: AWS_COMPLAINT_RATE_REVIEW,
} as const;

/**
 * How far back the headline rates look. Thirty days.
 *
 * Long enough that an ordinary sending week clears the volume floor, short
 * enough that a list cleaned last month stops being punished for it. It is also
 * roughly the horizon AWS reputation moves on, so a tenant watching this number
 * fall is watching the thing AWS is watching.
 */
export const OVERVIEW_ACTIVITY_WINDOW_DAYS = 30;

/* -------------------------------------------------------------------------- */
/* The volume floor — a rate that is allowed to exist                          */
/* -------------------------------------------------------------------------- */

/**
 * Below this many messages in a rate's denominator, **no rate is produced at
 * all** — on the Overview's tiles that denominator is settled outcomes (delivered
 * plus bounced) since slice 19, not accepted messages.
 *
 * A bounce rate computed from a handful of messages is noise wearing a
 * percentage sign. The floor is not a taste call, it falls out of the tighter
 * of the two thresholds: the smallest non-zero complaint rate a workspace can
 * possibly have is `1 / denominator`, so for a single complaint to be able to
 * land *under* the 0.1% review line at all, the denominator has to be at least
 * 1,000.
 * Below that the rate has exactly two reachable values — zero, or several times
 * the threshold — and a tile that flips between "perfect" and "critical" on one
 * event is worse than no tile.
 *
 * One floor governs both headline rates because they share a denominator and
 * are read side by side. Showing bounce rate while hiding complaint rate would
 * make the reader trust the visible one more than the volume supports.
 *
 * It is deliberately higher than {@link RATE_SIGNIFICANCE_MINIMUM}, which is
 * 100 and guards a *single campaign's* results. This screen aggregates a whole
 * workspace over a month and is the number somebody would pause sending over,
 * so it is held to the stricter test. In a guarded development environment,
 * and for a small tenant, this floor is almost never cleared — the screen then
 * shows counts and says why, which is the correct outcome rather than a gap in
 * coverage.
 */
export const OVERVIEW_MIN_VOLUME_FOR_RATE = 1_000;

/**
 * A rate the UI cannot accidentally render from six messages.
 *
 * `rate` is `null` — not `0`, not `NaN` — whenever the denominator is under the
 * floor, so a component that reaches for it gets nothing to format and has to
 * handle the honest case. The numerator and denominator ride along because
 * *that* is what the screen shows instead: "3 bounces out of 41 messages" is a
 * true sentence at any volume, and "7.3%" is not.
 */
export const rateReadingSchema = z
  .object({
    /** 0–1, or null when {@link RateReading.meaningful} is false. Never a zero stand-in. */
    rate: z.number().min(0).max(1).nullable(),
    /** The count on top — bounced, complained, delivered. Always shown. */
    numerator: z.number().int().min(0),
    /**
     * The messages the rate was computed over. Always shown. On the Overview's
     * sending tiles that is `settled` — the accepted emails Amazon has reported a
     * delivery or a bounce for — never everything accepted; see
     * {@link overviewSendingHealthSchema}.
     */
    denominator: z.number().int().min(0),
    /**
     * True only when `denominator` reached {@link OVERVIEW_MIN_VOLUME_FOR_RATE}.
     * "Only when", not "whenever": a reading over the floor can still be
     * withheld — `rate` null, `meaningful` false — when its denominator is known
     * to be lopsided; see {@link onlyBouncesReported}.
     */
    meaningful: z.boolean(),
    /** The floor this reading was tested against, so the UI can say the number. */
    minimumVolume: z.number().int().min(0),
  })
  .strict();
export type RateReading = z.infer<typeof rateReadingSchema>;

/**
 * The only supported way to make a {@link RateReading}.
 *
 * Pure, and shared rather than duplicated in the API and the web app, for the
 * reason `sesReadiness` is shared: two implementations of "is there enough
 * volume" drift, and the way they drift is that one of them stops checking.
 *
 * `minimum` is a parameter with a default rather than a hard-coded read so a
 * caller with a genuinely different floor — a per-campaign reading, say — uses
 * the same arithmetic instead of writing its own.
 */
export function rateReading(
  numerator: number,
  denominator: number,
  minimum: number = OVERVIEW_MIN_VOLUME_FOR_RATE,
): RateReading {
  const safeNumerator = Math.max(0, Math.trunc(numerator));
  const safeDenominator = Math.max(0, Math.trunc(denominator));
  const meaningful = safeDenominator >= minimum && safeDenominator > 0;
  return {
    // Clamped because a numerator larger than its denominator is a query bug,
    // and a 140% bounce rate on screen sends the reader after the wrong fault.
    rate: meaningful ? Math.min(1, safeNumerator / safeDenominator) : null,
    numerator: safeNumerator,
    denominator: safeDenominator,
    meaningful,
    minimumVolume: minimum,
  };
}

/* -------------------------------------------------------------------------- */
/* Health bands — warning has to arrive before the threshold                   */
/* -------------------------------------------------------------------------- */

/**
 * How a rate is doing against its threshold.
 *
 * Three bands and no fourth. "Not enough volume" is not a band — it is the
 * absence of one, expressed as `null` from {@link healthBand} — because a grey
 * fourth chip sitting in the same row as the green one reads as a rating, and
 * this screen must not rate what it did not measure.
 */
export const OVERVIEW_HEALTH_BAND_VALUES = ['ok', 'warning', 'critical'] as const;
export const healthBandSchema = z.enum(OVERVIEW_HEALTH_BAND_VALUES);
export type HealthBand = z.infer<typeof healthBandSchema>;

/**
 * Where `warning` starts, as a fraction of the threshold. Half.
 *
 * **A rate that has already crossed AWS's line is not a warning, it is an
 * incident.** By the time bounce rate reads 5% the account is already what AWS
 * is looking at, and a product whose first amber appears there has given the
 * tenant no time at all. Half the threshold is the last point at which the
 * usual repairs — stop the campaign, clean the list, re-check the segment —
 * can still land before AWS reacts: it means 2.5% of mail bouncing, or one
 * complaint in two thousand, both of which are already abnormal for a list the
 * tenant has permission to mail.
 *
 * A fraction rather than two more absolute constants, so that if AWS moves a
 * threshold the warning moves with it instead of silently becoming a second
 * critical.
 */
export const OVERVIEW_WARNING_FRACTION_OF_THRESHOLD = 0.5;

/**
 * `ok` | `warning` | `critical`, or `null` when there is no rate to band.
 *
 * Takes the reading rather than a bare number so it is impossible to band a
 * rate the floor refused: a `null` rate can only produce a `null` band, and the
 * component is forced to render the counts-and-reason branch instead of a
 * reassuring green chip earned by eleven messages.
 *
 * `critical` is `>=` the threshold, not `>`. Sitting exactly on AWS's review
 * line is not a pass.
 */
export function healthBand(reading: RateReading, threshold: number): HealthBand | null {
  if (reading.rate === null) return null;
  if (reading.rate >= threshold) return 'critical';
  return reading.rate >= threshold * OVERVIEW_WARNING_FRACTION_OF_THRESHOLD ? 'warning' : 'ok';
}

/**
 * A headline rate with everything the tile needs to be honest about it.
 *
 * The band is computed by the API with {@link healthBand} and carried on the
 * wire, rather than left for the component, so that the API's idea of
 * "concerning" and the screen's idea of "red" cannot come apart. The threshold
 * travels too: the tile draws the line it is being measured against.
 */
export const overviewRateKpiSchema = z
  .object({
    reading: rateReadingSchema,
    /** AWS's review threshold for this rate, as a fraction. */
    threshold: z.number().min(0).max(1),
    /** Null exactly when `reading.rate` is null. Never guessed. */
    band: healthBandSchema.nullable(),
  })
  .strict();
export type OverviewRateKpi = z.infer<typeof overviewRateKpiSchema>;

/** Builds the KPI, binding the reading to its threshold and its band in one place. */
export function overviewRateKpi(reading: RateReading, threshold: number): OverviewRateKpi {
  return { reading, threshold, band: healthBand(reading, threshold) };
}

/* -------------------------------------------------------------------------- */
/* KPIs 1–3 — bounce, complaint, deliverability                                */
/* -------------------------------------------------------------------------- */

/**
 * The sending-health block: three rates over one window, and the raw counts
 * that are true whether or not the rates are.
 *
 * **`settled` is the denominator of all three — since slice 19 — and `accepted`
 * is not.** `accepted` is the number slices 5 and 6 went to trouble to keep
 * separate from `delivered`: messages the provider took, which is not the same
 * as messages that arrived. It was the denominator, and that was right only while
 * every accepted message had been reported on. Reports arrive asynchronously,
 * minutes to hours after a send, so a window mid-report divided by `accepted`
 * reads clean: twelve outcomes back from 1,116 accepted banded `0 / 1,116` as a
 * green 0% — a verdict about 1,104 messages nobody had reported on. Slice 18's
 * halt measures over settled outcomes for exactly this reason, and the tiles now
 * do the same: `settled` is `delivered + bounced`, the accepted messages Amazon
 * has reported a terminal outcome for, and the floor is tested against it. The
 * accepted messages still waiting for a terminal event — `awaitingEvents` — are
 * said beside every rate rather than dissolved into it. (A rejection or a
 * rendering failure is neither settled nor waiting: it is counted with the
 * reasons a message did not arrive.) `deliverability` is delivered over settled, the share of
 * reported outcomes that arrived, and it is the third KPI rather than the first
 * because a low delivery rate is usually a symptom of the two rates above it.
 *
 * `bounces` and `notDelivered` are both here and do not agree, deliberately:
 * one splits bounced messages the way the list has to be repaired, the other
 * splits every reason a message did not arrive, including the two that never
 * became bounces. Neither is summed into a single failure figure.
 *
 * **Every member of this block is EMAIL, and the screen says so.** The two
 * headline rates are the ones AWS reviews an account against, and the
 * vocabulary throughout is SES's.
 *
 * **Slice 12 removed the accident that used to make that safe, so read the next
 * paragraph before touching the query.** This block once justified itself with
 * {@link DELIVERY_MEASURED_CHANNELS}: only email produced delivery events, so
 * only email could reach a numerator, and the cohort filter was belt as well as
 * braces. That is no longer true. WhatsApp reports `delivered` and `failed`
 * now, and that constant names both channels — so the old sentence, read today,
 * argues that the `messages.channel = 'email'` filter in `overview-sending.ts`
 * is redundant.
 *
 * **It is not. It is now the only thing standing between a WhatsApp status and
 * an Amazon figure.** Slice 10 shipped precisely that bug and it was critical:
 * a workspace sitting exactly on AWS's 5% bounce line rendered as 1.55% behind
 * a green badge, because two thousand WhatsApp sends were sitting in the
 * denominator of a rate Amazon measures against email. The cohort is filtered
 * at the source rather than caveated in copy — see `overview-sending.ts`, and
 * {@link isBounceMeasured} for the predicate that says which channel has a
 * bounce at all.
 *
 * **Every count here counts MESSAGES, not provider notifications.** SES sends
 * as many rows as it likes about one message — a `DeliveryDelay` per retry, an
 * asynchronous second bounce, an SNS re-delivery under a fresh `MessageId` the
 * replay guard cannot recognise — so `bounces`, `notDelivered` and `complaints`
 * are all `count(distinct message_id)`. Counted as rows, `bounces` stops adding
 * up to `bounced` and a duplicated complaint notification can push
 * `complaintRate` over AWS's line on its own.
 *
 * **There is no engagement member on this block, and slice 11 deliberately did
 * not add one.** Opens and clicks are directional — wrong in both directions at
 * once — and this block is where the two rates AWS reviews an account against
 * live. Putting a number nobody can stand behind in the same object as the two
 * that can suspend a tenant's AWS account is how the exact ones stop being read
 * as exact. `engagementRateSchema` in `./engagement.js` is not even assignable
 * to {@link OverviewRateKpi}, which is that argument made in types.
 */
export const overviewSendingHealthSchema = z
  .object({
    /**
     * Email messages the provider took in the window — email-only, because every
     * numerator is. **Not the denominator of any rate since slice 19**; that is
     * `settled`. See the header.
     */
    accepted: z.number().int().min(0),
    delivered: z.number().int().min(0),
    bounced: z.number().int().min(0),
    /**
     * `delivered + bounced`: the accepted emails Amazon has reported a terminal
     * outcome for. **The denominator of every rate below.** See the block's header.
     */
    settled: z.number().int().min(0),
    complaints: z.number().int().min(0),
    /**
     * Accepted, still waiting for a terminal event — the unsettled remainder, said
     * beside every rate. The number that should trend to zero, except for email
     * sent before delivery reports were set up, which never gets one and stays
     * here until it leaves the window.
     */
    awaitingEvents: z.number().int().min(0),
    /** KPI 1. Against {@link OVERVIEW_RATE_THRESHOLDS}`.bounce`. */
    bounceRate: overviewRateKpiSchema,
    /** KPI 2. Against {@link OVERVIEW_RATE_THRESHOLDS}`.complaint`. */
    complaintRate: overviewRateKpiSchema,
    /**
     * KPI 3. `delivered / settled` — no threshold, because AWS does not
     * publish one and inventing a line for it would give a made-up number the
     * same visual weight as AWS's two real ones.
     */
    deliverability: rateReadingSchema,
    bounces: bounceBreakdownSchema,
    notDelivered: notDeliveredBreakdownSchema,
  })
  .strict();
export type OverviewSendingHealth = z.infer<typeof overviewSendingHealthSchema>;

/* -------------------------------------------------------------------------- */
/* Accepted, and nothing ever came back — the fourth thing a zero can be        */
/* -------------------------------------------------------------------------- */

/**
 * True when messages were accepted and **not one of them has a delivery outcome
 * yet** — no delivery, no bounce, no failure.
 *
 * ## WHY THIS EXISTS: THE LIVE DEPLOYMENT, AND THE ZERO THAT WAS NOT ONE
 *
 * Slice 20 set out to keep three things apart — zero, no data, and a day before
 * the workspace existed — and found a fourth only by proving the dashboard
 * against the deployment that actually sends. That deployment has accepted over
 * a thousand real emails and **no delivery event has ever arrived from SES**:
 * SNS is not wired (slice 19). Every accepted message therefore sits in
 * `awaitingEvents`, and every outcome count is `0` — not because nothing
 * bounced, but because nobody has said.
 *
 * Over those numbers the parked dashboard drew exactly what slice 0's sentence
 * warns about. `accepted` cleared {@link OVERVIEW_MIN_VOLUME_FOR_RATE}, so
 * {@link rateReading} produced a real `0` and {@link healthBand} banded it
 * `ok`: **"0.00% — Under the line"** in green, over a month in which Wend had
 * no idea whether a single address existed. The daily bounce and complaint
 * charts drew a row of zero stubs under Amazon's dashed line, and the WhatsApp
 * chart's all-zero branch would have said *no message failed to be delivered*.
 * Quiet good news, three times, about a list nobody had measured.
 *
 * The volume floor does not catch it and was never meant to: it asks whether
 * there are *enough messages* for a rate to mean something, not whether any of
 * them has an outcome. `accepted` is a fact from this database; `bounced` is a
 * fact only once SES has reported on the message.
 *
 * ## WHY THE TEST IS "NONE", AND WHAT IT DELIBERATELY DOES NOT DECIDE
 *
 * `awaiting >= accepted` — every accepted message still waiting — is the one
 * state in which every delivery outcome count is *certainly* unmeasured,
 * whatever the rate machinery says. `>=` rather than `===` because
 * `awaitingEvents` is derived by subtraction and clamped, and a count that
 * somehow ran ahead of `accepted` is still a cohort with nothing reported.
 * `accepted > 0` because a workspace that sent nothing is the "nothing sent yet"
 * arm, which is a different sentence.
 *
 * **A complaint is a report — and it is still not an outcome this predicate
 * gates.** A complaint moves no message status, so a window whose only reports
 * are complaints has every message awaiting. Slice 19 first answered that by
 * making a complaint end this state, and review found what that drew: the tiles,
 * the not-delivered card and the charts each went back to their reported arms,
 * so the card printed **Delivered 0, Bounced 0** and five reasons at `0` as
 * facts, and the bounce chart said none had bounced — over a window in which
 * Amazon had reported not one delivery or bounce. So the predicate is about the
 * outcomes it hides — deliveries and bounces — and the complaint is honoured
 * where it is a fact: each of those screens prints the complaint count beside
 * the sentence, and the sentence becomes {@link NO_OUTCOME_BUT_COMPLAINTS_COPY},
 * which does not claim no complaint came back.
 *
 * **The partial case is closed too, one layer down.** Twelve outcomes back from
 * 1,116 accepted used to band `0 / 1,116` as a green rate; the rates now divide
 * by `settled` (see {@link overviewSendingHealthSchema}), so twelve outcomes is
 * twelve under the floor and no verdict at all. This predicate remains the
 * certain half: nothing reported, so no figure of any kind.
 *
 * Pure and shared, for `rateReading`'s reason: the tiles, the not-delivered card
 * and both charts ask it, and four private versions of "has anything come back"
 * would drift in the direction of one of them forgetting.
 */
export function noOutcomeReported(counts: {
  readonly accepted: number;
  readonly awaiting: number;
}): boolean {
  return counts.accepted > 0 && counts.awaiting >= counts.accepted;
}

/**
 * Bounces reported, **not one delivery**, and messages still waiting — the one
 * shape in which the settled denominator is known to be lopsided.
 *
 * Every rate on the Overview divides by `delivered + bounced`. When deliveries
 * are not being reported at all, that sum is the bounces alone, and every rate
 * over it reads as if everything that was reported on bounced: a red **100% —
 * Over the line** about a list whose deliveries nobody is reporting. It is a
 * reachable shape, not a theoretical one: Wend ingests an identity's own SES
 * notifications as well as a configuration set's events, and an identity set up
 * to notify bounces and complaints — the usual setup before slice 19 — reports
 * exactly this. So the API withholds the three readings in this state and the
 * tiles say why, with the counts.
 *
 * **`awaiting > 0` is what keeps a dead list red.** A list whose every accepted
 * message has an outcome and none was delivered really did bounce, and 100% is
 * then the truth — the one figure this must never hide. Deliveries that are not
 * being reported leave their messages waiting, so the lopsided shape always has
 * some.
 *
 * Asked of whole windows (`overview-sending.ts`) and of single days
 * (`dailyThresholdCount`), which have the same three counts.
 */
export function onlyBouncesReported(counts: {
  readonly delivered: number;
  readonly bounced: number;
  readonly awaiting: number;
}): boolean {
  return counts.delivered === 0 && counts.bounced > 0 && counts.awaiting > 0;
}

/**
 * What the dashboard says instead of a rate, a count of zero or a chart, when
 * {@link noOutcomeReported} is true. Per channel, because the provider who has
 * not reported is different and so is the repair.
 *
 * `short` is honesty about a number, so it is visible and four words long in
 * spirit. `detail` is printed beside it rather than behind a disclosure: on the
 * live deployment this is the *only* thing the outcome half of the page has to
 * say, and a skimmer never opens a tooltip.
 *
 * The cause is hedged on purpose, because this state is also the first minutes
 * after a first send, and naming SNS as the fault would be a confident claim
 * about a workspace Wend has not diagnosed. **But the hedge must not read as
 * "wait".** The first wording ended the short line in "yet" and offered "or none
 * has arrived yet" as an equal explanation; on the live deployment, weeks after
 * the first send, that told a non-technical reader to keep waiting for reports
 * that were never going to come. The predicate cannot see how old the oldest
 * waiting message is, so the sentence gives the reader the rule instead: reports
 * take minutes, and silence past an hour is a setup problem. Review found it.
 *
 * **"Probably", and the second review is why.** The predicate is `awaiting >=
 * accepted`, and a message Amazon has reported only as *delayed* — or Meta only
 * as *sent* — is still awaiting. So this can be true on a workspace whose
 * notifications arrive perfectly, with every message stuck at a receiving server
 * that is slow. Stated flatly, it sent that tenant to fix a setup that works.
 * The not-delivered card prints the delayed count beside it when there is one.
 */
export const NO_OUTCOME_REPORTED_COPY: Readonly<
  Record<Channel, { readonly short: string; readonly detail: string }>
> = {
  email: {
    short: 'No delivery report has come back from Amazon.',
    detail:
      'Amazon accepted these emails, but no report of a delivery, a bounce or a complaint has reached Wend for any of them — so there is no bounce rate, no complaint rate and no count of what did not arrive, not zero ones. Reports normally arrive within minutes of a send. If these emails went out more than an hour ago and this still says so, Amazon’s delivery notifications are probably not reaching Wend — a setup step, not something waiting will fix. Until they do, a clean list and a dead one look the same from here.',
  },
  whatsapp: {
    short: 'No delivery report has come back from Meta.',
    detail:
      'Meta accepted these WhatsApp messages, but no report of a delivery or a failure has reached Wend for any of them — so there is no count of what could not be delivered, not a zero one. Reports normally arrive within minutes of a send. If these messages went out more than an hour ago and this still says so, Meta’s status updates are probably not reaching Wend — a setup step, not something waiting will fix.',
  },
};

/**
 * {@link NO_OUTCOME_REPORTED_COPY}'s email sentence for a window whose only
 * reports are **complaints**. That one says no report "of a delivery, a bounce or
 * a complaint" came back, which is false here, and review found it printed beside
 * a complaint count.
 *
 * It keeps what is still true — no delivery or bounce, so no rate and no count of
 * what did not arrive — and says the part a complaint proves: Amazon's reports
 * do reach Wend. The likelier gap is then which mail they are *about*, so the
 * detail names the two ways email goes out with no delivery reports at all. Email
 * only: Meta has no complaint.
 */
export const NO_OUTCOME_BUT_COMPLAINTS_COPY = {
  short: 'Amazon has reported spam complaints, but no delivery or bounce.',
  detail:
    'People marked some of these emails as spam and Amazon told Wend, so its reports do reach Wend. But no delivery or bounce has been reported for any of them — so there is no bounce rate, no complaint rate and no count of what did not arrive, not zero ones. Amazon reports deliveries and bounces for this workspace’s email only once delivery reports are set up on the Integrations screen, and never for email sent before they were.',
} as const;

/* -------------------------------------------------------------------------- */
/* Snapshot age — the price of never calling a provider                        */
/* -------------------------------------------------------------------------- */

/**
 * After this long, a stored check is presented as old. Twenty-four hours.
 *
 * A day is roughly how long a provider fact stays safe to repeat: AWS grants
 * production access, Meta flags a number, an identity gets paused, and none of
 * those events tell Wend anything. The banner does not claim the snapshot is
 * wrong — it says how old it is and lets the reader decide, which is the whole
 * difference between a stale dashboard and a lying one.
 */
export const OVERVIEW_SNAPSHOT_STALE_AFTER_MS = 24 * 60 * 60 * 1_000;

/**
 * When a stored provider check was taken, and how that reads now.
 *
 * `checkedAt` is null when the integration has never been checked successfully,
 * which is a third state and not "very old": there is nothing to be stale.
 * `ageMs` is null in the same case.
 */
export const overviewSnapshotSchema = z
  .object({
    /** `integrations.last_check.checkedAt`. Null when no check has ever succeeded. */
    checkedAt: isoDateTimeSchema.nullable(),
    /** Age at the moment the overview was generated. Null when never checked. */
    ageMs: z.number().int().min(0).nullable(),
    /** True once `ageMs` passes {@link OVERVIEW_SNAPSHOT_STALE_AFTER_MS}. */
    stale: z.boolean(),
  })
  .strict();
export type OverviewSnapshot = z.infer<typeof overviewSnapshotSchema>;

/**
 * Derives the snapshot age. Never-checked is `stale: false`, because a
 * "checked a long time ago" warning over an integration nobody has ever tested
 * points at the wrong repair — the repair there is to connect it, which the
 * readiness state already says.
 */
export function snapshotStaleness(
  checkedAt: string | null,
  now: Date = new Date(),
): OverviewSnapshot {
  if (checkedAt === null) return { checkedAt: null, ageMs: null, stale: false };
  const at = Date.parse(checkedAt);
  // An unparseable timestamp is our own column being wrong. Treated as unknown
  // age rather than as fresh: claiming freshness is the failure mode worth
  // avoiding, and a null age forces the screen to say "unknown".
  if (Number.isNaN(at)) return { checkedAt, ageMs: null, stale: false };
  const ageMs = Math.max(0, now.getTime() - at);
  return { checkedAt, ageMs, stale: ageMs >= OVERVIEW_SNAPSHOT_STALE_AFTER_MS };
}

/* -------------------------------------------------------------------------- */
/* KPI 4 — can this workspace send at all                                      */
/* -------------------------------------------------------------------------- */

/**
 * Quota with AWS's `-1` translated exactly once.
 *
 * SESv2 reports an account with no 24-hour cap as `max24HourSend: -1`, and
 * every consumer that forgets is one division away from rendering "-40,000%
 * used" or, worse, a full-looking bar on the healthiest possible account. The
 * translation happens here, in {@link sesQuotaUsage}, and `cap` is `null` for
 * unlimited so the arithmetic cannot be attempted downstream.
 */
export const sesQuotaUsageSchema = z
  .object({
    /** Messages per 24 hours, or null for an account AWS does not cap. */
    cap: z.number().int().min(0).nullable(),
    sentLast24Hours: z.number().int().min(0),
    /** 0–1. Null when there is no cap to be a fraction of. */
    usedFraction: z.number().min(0).max(1).nullable(),
    /** True when AWS reported no cap. The good case, and it must not look like the bad one. */
    unlimited: z.boolean(),
  })
  .strict();
export type SesQuotaUsage = z.infer<typeof sesQuotaUsageSchema>;

/** Turns a stored {@link SesQuota} into something safe to draw a bar from. */
export function sesQuotaUsage(quota: SesQuota): SesQuotaUsage {
  const unlimited = quota.max24HourSend < 0;
  const cap = unlimited ? null : Math.max(0, Math.trunc(quota.max24HourSend));
  const sent = Math.max(0, Math.trunc(quota.sentLast24Hours));
  return {
    cap,
    sentLast24Hours: sent,
    // Clamped: AWS's two figures are sampled independently and `sent` can
    // briefly exceed `cap`. A bar past its end reads as a rendering fault.
    usedFraction: cap === null || cap === 0 ? null : Math.min(1, sent / cap),
    unlimited,
  };
}

/**
 * Whether email can leave this workspace, from the stored SES snapshot.
 *
 * `connected: false` is the ordinary starting state, not an error, and every
 * other field is null there — the same argument `integrationResponseSchema`
 * makes by answering null rather than 404.
 *
 * The sandbox is called out on its own rather than folded into `state` alone,
 * because it is the single most common cause of "the product is broken": a
 * sandboxed account refuses every message to an address that is not verified
 * with Amazon, so a campaign to real customers fails recipient by recipient
 * while the account itself reads as connected and working.
 */
export const overviewSesSendabilitySchema = z
  .object({
    connected: z.boolean(),
    /** From `sesReadiness()` over the stored check. Null when not connected. */
    state: sesReadinessStateSchema.nullable(),
    /**
     * Still in the SES sandbox. Reaches only verified addresses.
     *
     * `null` when the key could not call `ses:GetAccount`, so AWS never said.
     * Read {@link accountVisible} before this: `!sandboxed` is `true` for
     * `null` and would paint a green "not sandboxed" badge over an account
     * nobody has looked at.
     */
    sandboxed: z.boolean().nullable(),
    /**
     * Verified **and** enabled — things a campaign could actually send from.
     *
     * `null` when `ses:ListEmailIdentities` was refused. Not `0`: a tile
     * reading "0 sending identities" is a setup problem somebody must fix, and
     * printing it over an account nobody was allowed to read sends a tenant
     * hunting a fault that is not there.
     */
    sendableIdentityCount: z.number().int().min(0).nullable(),
    /**
     * AWS's account-level send switch. False means AWS has paused this account;
     * `null` means `ses:GetAccount` was refused. `!sendingEnabled` is a bug.
     */
    sendingEnabled: z.boolean().nullable(),
    /**
     * Whether `GetAccount` answered on the last successful check.
     *
     * False both when nothing is connected and when the key lacks the
     * permission — `connected` separates those, and a tile must read it first
     * anyway. What this adds is the difference between a quota Wend has never
     * been allowed to read and a quota that is genuinely absent.
     */
    accountVisible: z.boolean(),
    quota: sesQuotaUsageSchema.nullable(),
    /** How old the facts above are. Never a live call — see the file header. */
    snapshot: overviewSnapshotSchema,
    /**
     * The integration's last connection test did not succeed — its row's
     * `status` is `failed`. **Independent of `snapshot`, and it has to be**: a
     * connect whose test failed stores no check at all, so it looked exactly
     * like one never tested ("nothing is known to be wrong"); and a re-test that
     * fails keeps the last good check, so a rotated key read as healthy. Review
     * found both.
     */
    lastCheckFailed: z.boolean(),
  })
  .strict();
export type OverviewSesSendability = z.infer<typeof overviewSesSendabilitySchema>;

/**
 * Whether WhatsApp can leave this workspace, from the stored Meta snapshot.
 *
 * `tokenExpiry` is the field this shape exists for. A Meta system user token
 * expires quietly and takes the channel down with it, and the repair — minting
 * a replacement in Business Manager — is somebody's errand that needs notice.
 *
 * `expiresAt: null` is ambiguous at Meta and stays ambiguous here:
 * `tokenIntrospected` is what tells "never expires" apart from "Meta would not
 * say", and a screen must not paint the second as a permanent green token.
 */
export const overviewWhatsAppSendabilitySchema = z
  .object({
    connected: z.boolean(),
    /** From `metaReadiness()` over the stored check. Null when not connected. */
    state: metaReadinessStateSchema.nullable(),
    /** `never` | `expiring` | `expired`. Null when not connected. */
    tokenExpiry: metaTokenExpiryStateSchema.nullable(),
    /** When the token dies. Null for one that never does — or that Meta would not discuss. */
    tokenExpiresAt: isoDateTimeSchema.nullable(),
    /** False means `tokenExpiresAt: null` is "unknown", not "never". */
    tokenIntrospected: z.boolean(),
    snapshot: overviewSnapshotSchema,
    /** As on the email arm: the last test failed, whatever the snapshot says. */
    lastCheckFailed: z.boolean(),
  })
  .strict();
export type OverviewWhatsAppSendability = z.infer<typeof overviewWhatsAppSendabilitySchema>;

export const overviewSendabilitySchema = z
  .object({
    ses: overviewSesSendabilitySchema,
    whatsapp: overviewWhatsAppSendabilitySchema,
  })
  .strict();
export type OverviewSendability = z.infer<typeof overviewSendabilitySchema>;

/* -------------------------------------------------------------------------- */
/* KPI 5 — credits, in messages                                                */
/* -------------------------------------------------------------------------- */

/**
 * Credits for one channel, plus what that is in messages.
 *
 * "1,000 credits" answers nothing on its own; "about 1,000 more emails"
 * answers the question the reader actually has. The conversion is
 * {@link CREDITS_PER_MESSAGE} — currently 1, defined once in `./credit.js`,
 * imported here and **not** re-guessed — and `creditsPerMessage` is carried on
 * the wire so the screen prints the same ratio the send path reserved at rather
 * than a constant a component compiled against months ago.
 *
 * `available` is `balance − reserved`, computed by `availableCredits()`. It is
 * on the wire because it is what the reader needs, and it is derived rather
 * than stored for the reason `./credit.js` gives: there is no third number.
 *
 * The vocabulary rule from `./credit.js` holds here and is worth repeating
 * because this is a screen: no field may be called cost, spend, charge or
 * price. A credit is Wend's metering unit and has no exchange rate with the
 * tenant's AWS or Meta invoice, and a column headed "Spend" over a number that
 * is not money is a billing dispute waiting to happen.
 */
export const overviewChannelCreditsSchema = z
  .object({
    channel: channelSchema,
    balance: z.number().int().min(0),
    reserved: z.number().int().min(0),
    /** `balance − reserved`. Never negative — see `availableCredits()`. */
    available: z.number().int().min(0),
    /** Roughly how many more messages `available` buys on this channel. */
    messagesRemaining: z.number().int().min(0),
    /** The ratio used, so the tile need not hard-code one. */
    creditsPerMessage: z.number().int().min(1),
  })
  .strict();
export type OverviewChannelCredits = z.infer<typeof overviewChannelCreditsSchema>;

/**
 * Builds one channel's credit tile from the stored balance row.
 *
 * A zero row is a real answer and must render as zero rather than as absence —
 * the same reason `zeroCreditBalances()` exists — so the API is expected to
 * call this for every channel, not only the ones with a row.
 */
export function overviewChannelCredits(
  channel: Channel,
  amounts: CreditAmounts,
): OverviewChannelCredits {
  const available = availableCredits(amounts);
  return {
    channel,
    balance: amounts.balance,
    reserved: amounts.reserved,
    available,
    // Floored, because a partial message is not a message a tenant can send.
    messagesRemaining: Math.floor(available / CREDITS_PER_MESSAGE),
    creditsPerMessage: CREDITS_PER_MESSAGE,
  };
}

/* -------------------------------------------------------------------------- */
/* KPI 6 — the suppression list, and where it is going                         */
/* -------------------------------------------------------------------------- */

/**
 * The window each half of the suppression trend covers. Thirty days.
 *
 * Matched to {@link OVERVIEW_ACTIVITY_WINDOW_DAYS} so the trend and the rates
 * describe the same stretch of time — two windows of different lengths on one
 * screen is how a reader concludes the wrong thing from a true pair of numbers.
 * `suppressions.created_at` is what makes this computable, which is why slice
 * 10 needs no migration to show it.
 */
export const OVERVIEW_SUPPRESSION_WINDOW_DAYS = 30;

/**
 * How many additions the recent window must differ by before the trend claims a
 * direction.
 *
 * Two suppressions last month and three this month is not a rising trend, but a
 * naive comparison calls it "+50%". Five is the smallest movement that is
 * unlikely to be one bad address and a coincidence; below it the direction is
 * `flat` and the screen shows the two counts, which are true regardless.
 */
export const OVERVIEW_TREND_MIN_MOVEMENT = 5;

export const OVERVIEW_TREND_DIRECTION_VALUES = ['rising', 'falling', 'flat'] as const;
export const trendDirectionSchema = z.enum(OVERVIEW_TREND_DIRECTION_VALUES);
export type TrendDirection = z.infer<typeof trendDirectionSchema>;

/**
 * Two adjacent windows of suppression additions, compared.
 *
 * **This is the one genuinely predictive number on the screen.** The list size
 * says where a tenant is; the direction of additions says where they are going,
 * and a rising line usually precedes the bounce and complaint rates rising —
 * often because a list was bought, or an old one was re-imported.
 *
 * `changeRatio` is null when the previous window was empty, because the growth
 * from zero is not a percentage. The counts are always there to fall back on.
 */
export const suppressionTrendSchema = z
  .object({
    windowDays: z.number().int().min(1),
    /** Additions in the most recent window. */
    recent: z.number().int().min(0),
    /** Additions in the window immediately before it. */
    previous: z.number().int().min(0),
    /** `recent − previous`. Signed, and the only number that needs no caveat. */
    delta: z.number().int(),
    /** `(recent − previous) / previous`. Null when `previous` is 0. */
    changeRatio: z.number().nullable(),
    direction: trendDirectionSchema,
  })
  .strict();
export type SuppressionTrend = z.infer<typeof suppressionTrendSchema>;

/** The only supported way to build the trend. Shared so the two sides agree on `flat`. */
export function suppressionTrend(
  recent: number,
  previous: number,
  windowDays: number = OVERVIEW_SUPPRESSION_WINDOW_DAYS,
): SuppressionTrend {
  const safeRecent = Math.max(0, Math.trunc(recent));
  const safePrevious = Math.max(0, Math.trunc(previous));
  const delta = safeRecent - safePrevious;
  const direction: TrendDirection =
    Math.abs(delta) < OVERVIEW_TREND_MIN_MOVEMENT ? 'flat' : delta > 0 ? 'rising' : 'falling';
  return {
    windowDays,
    recent: safeRecent,
    previous: safePrevious,
    delta,
    changeRatio: safePrevious === 0 ? null : delta / safePrevious,
    direction,
  };
}

export const suppressionChannelCountSchema = z
  .object({ channel: channelSchema, count: z.number().int().min(0) })
  .strict();
export type SuppressionChannelCount = z.infer<typeof suppressionChannelCountSchema>;

/**
 * The same movement, per channel — added by slice 12, and it had to be.
 *
 * The whole-list {@link SuppressionTrend} above is what the Overview card
 * prints, and until slice 12 the sentence beside it could honestly say what it
 * says: *rising suppressions are what a bounce rate crossing the AWS line looks
 * like a fortnight before it happens, because every hard bounce writes a row
 * here on the way to becoming part of that rate.* Every reason a `whatsapp` row
 * could carry was `manual` or `unsubscribe` — low-volume and tenant-initiated —
 * so the movement was email's movement.
 *
 * Slice 12 makes `hard_bounce` reachable on `whatsapp` at campaign volume: a
 * Meta 131026 means a number will never receive a WhatsApp message, and a
 * landline-heavy list produces hundreds of them in one afternoon. Folded into
 * one figure, that reads on screen as a bounce problem in a mailbox provider
 * that has not moved at all — and the inverse is worse, because once a reader
 * learns the trend moves for WhatsApp reasons, a genuine SES spike hiding inside
 * the same number gets dismissed.
 *
 * So the movement is split the way `byChannel` already splits the total, and the
 * card attributes the sentence rather than dropping it. It is a separate field
 * rather than a member of {@link SuppressionChannelCount}, because the total is
 * all-time and cumulative while the trend is windowed — one object carrying both
 * would invite the sum of two different time bases.
 */
export const suppressionChannelTrendSchema = z
  .object({ channel: channelSchema, trend: suppressionTrendSchema })
  .strict();
export type SuppressionChannelTrend = z.infer<typeof suppressionChannelTrendSchema>;

/**
 * Counts by reason, and the reason is the actionable half.
 *
 * `unsubscribe` is a healthy list working; `hard_bounce` is list quality;
 * `complaint` is a targeting or consent problem and the one that costs a tenant
 * their AWS account. One total hides which of the three is growing.
 */
export const suppressionReasonCountSchema = z
  .object({ reason: suppressionReasonSchema, count: z.number().int().min(0) })
  .strict();
export type SuppressionReasonCount = z.infer<typeof suppressionReasonCountSchema>;

export const overviewSuppressionSummarySchema = z
  .object({
    /** Every suppression this workspace holds, all channels, all reasons. */
    total: z.number().int().min(0),
    /** One entry per channel, zeros included. Absence and zero must not look alike. */
    byChannel: z.array(suppressionChannelCountSchema),
    /** One entry per reason, zeros included. */
    byReason: z.array(suppressionReasonCountSchema),
    /** The whole list's movement, every channel folded together. */
    trend: suppressionTrendSchema,
    /**
     * The same movement per channel, zeros included. **Not decoration:** it is
     * what lets the card attribute a rise to the channel it happened on instead
     * of to the one its sentence used to name. See
     * {@link suppressionChannelTrendSchema}.
     */
    trendByChannel: z.array(suppressionChannelTrendSchema),
  })
  .strict();
export type OverviewSuppressionSummary = z.infer<typeof overviewSuppressionSummarySchema>;

/* -------------------------------------------------------------------------- */
/* KPI 7 — the last few campaigns                                              */
/* -------------------------------------------------------------------------- */

/**
 * How many campaigns the Overview lists. Five.
 *
 * Enough to see a pattern across a week's sending, few enough that the list
 * stays a summary and not the campaigns screen with a worse filter. The link on
 * each row is the point: this block exists to get somebody to the campaign,
 * not to replace it.
 */
export const OVERVIEW_RECENT_CAMPAIGN_LIMIT = 5;

/**
 * {@link noOutcomeReported}, for one campaign row — which carries no `awaiting`
 * count, so it is asked of what the row does carry: accepted, and not one
 * message reported delivered, nor (where the channel measures them) bounced.
 *
 * Here rather than in the card that asks it. The card had its own copy of the
 * rule and its own two sentences, and review found both: a third private
 * version of "has anything come back" is the drift {@link noOutcomeReported}
 * exists to prevent.
 */
export function campaignNoOutcomeReported(campaign: {
  readonly accepted: number;
  readonly delivered: number | null;
  readonly bounced: number | null;
}): boolean {
  return (
    campaign.accepted > 0 &&
    campaign.delivered === 0 &&
    (campaign.bounced === null || campaign.bounced === 0)
  );
}

/**
 * What the suppression card adds under its trend when {@link noOutcomeReported}
 * is true of the workspace's email.
 *
 * The card calls its trend the early warning for a bounce problem, and every
 * email hard bounce and complaint reaches the list only through Amazon's
 * reports. With none arriving, the email half of the trend cannot move for a
 * bounce at all, and the card was printing "Level with the 30 days before" over
 * a cold list — the reassuring version of a number nobody measured. Review
 * found it.
 */
export const SUPPRESSION_EMAIL_RISE_UNREPORTED_COPY =
  'Email suppressions are rising, but until Amazon’s reports reach Wend no bounce or complaint can add one — so this rise is unsubscribes and addresses added by hand.';

/**
 * The card's standing sentence for an unreported workspace — see
 * {@link SUPPRESSION_EMAIL_RISE_UNREPORTED_COPY}, which replaces the email
 * rise's "what a bounce problem looks like" in the same state, because the two
 * side by side contradicted each other (review).
 */
export const SUPPRESSION_BOUNCES_UNREPORTED_COPY =
  'No delivery report has come back from Amazon, and email bounces and spam complaints reach this list only through those reports — so the email side of this trend cannot show a bounce problem.';

/** What a recent-campaign row says when {@link campaignNoOutcomeReported} is true. */
export const RECENT_CAMPAIGN_NO_OUTCOME_COPY = {
  withBounces: 'None of these has been reported delivered or bounced.',
  deliveredOnly: 'None of these has been reported delivered.',
} as const;

/**
 * What a recent-campaign row says when the only reports back are bounces —
 * {@link onlyBouncesReported}, slice 24 item 18.
 *
 * It names the number that **is** known and does not claim the one that is not.
 * "Delivered 0" beside a real bounce count reads as a measurement; what is true
 * is that nothing has reported a delivery yet and some of this campaign is still
 * in flight.
 *
 * A function rather than a constant because both figures belong in it: a reader
 * deciding whether to worry needs to know whether two bounced out of five still
 * waiting or two out of two thousand.
 */
export function recentCampaignBouncesOnly(bounced: number, awaiting: number): string {
  return (
    `${String(bounced)} ${bounced === 1 ? 'has' : 'have'} bounced and nothing has been reported delivered yet, ` +
    `with ${String(awaiting)} still to report. A delivered count would be an absence, not a zero.`
  );
}

/**
 * One row of the recent-campaigns list.
 *
 * `accepted`, `delivered` and `bounced` and nothing else — three numbers that
 * fit on a row and answer "did that one go out cleanly". No rate, because a
 * single campaign's rate is usually below the volume floor and a row is not
 * where the caveat fits; the reader clicks through to the results screen, which
 * has room to say it.
 *
 * `sentAt` is `max(messages.sent_at)` — the acceptance stamp of the last
 * message that went out, because `campaigns` has no completion column and this
 * slice adds no migration. It is null exactly while **nothing has been
 * accepted**, which is wider than "has not started": a campaign whose every
 * recipient was skipped reaches `sent` with no messages at all and still
 * reports null. The reader is told which of the two it is by `status`, so a row
 * must not translate a null into "not sent yet" without looking there — a green
 * *Sent* badge beside "not sent yet" is two contradictory claims about one
 * campaign. `scheduledAt` is deliberately not substituted: "when it went" and
 * "when it was meant to go" are different facts, and a list that mixes them
 * makes a late campaign look punctual.
 *
 * `delivered` and `bounced` are **null on a channel where nothing measures
 * them** — see {@link isDeliveryMeasured}. Only email produces `delivery_events`
 * in this release, and a WhatsApp row printing `Delivered 0` beside an email
 * row printing `Delivered 471` tells the reader the WhatsApp send reached
 * nobody when the truth is that Wend never looked. Null is on the wire rather
 * than a zero plus a flag for the same reason `rateReading` puts `rate: null`
 * there: a component cannot accidentally format what it has not got.
 */
export const overviewRecentCampaignSchema = z
  .object({
    id: uuidSchema,
    name: z.string(),
    channel: channelSchema,
    status: campaignStatusSchema,
    /** When the last message was accepted. Null when none ever was — see above. */
    sentAt: isoDateTimeSchema.nullable(),
    /** Messages the provider took. Measured on every channel; not the same as delivered. */
    accepted: z.number().int().min(0),
    /** Null on a channel with no delivery measurement — never a stand-in zero. */
    delivered: z.number().int().min(0).nullable(),
    /**
     * Accepted messages this campaign is still waiting on — `accepted` minus
     * what has settled, where settling is a delivery or a bounce.
     *
     * **Slice 24, item 18.** The row could ask `campaignNoOutcomeReported`, which
     * catches the campaign nothing has been reported for at all, and could not
     * ask {@link onlyBouncesReported}, which catches the shape in between: some
     * bounces back, no deliveries yet, and the rest still in flight. Without it
     * the row printed "Delivered 0" beside a real bounce count — the same "a zero
     * that means not measured" the Overview tiles were fixed for one slice
     * earlier, on the card directly below them.
     *
     * Null wherever `delivered` is null, because a channel that reports nothing
     * per message has nothing to be waiting for.
     */
    awaiting: z.number().int().min(0).nullable(),
    /** Null on a channel with no delivery measurement — never a stand-in zero. */
    bounced: z.number().int().min(0).nullable(),
  })
  .strict();
export type OverviewRecentCampaign = z.infer<typeof overviewRecentCampaignSchema>;

/* -------------------------------------------------------------------------- */
/* Pillars — saying "not built" out loud                                       */
/* -------------------------------------------------------------------------- */

/**
 * What the Overview is allowed to say about a product pillar.
 *
 *   - `available` — built, and this workspace is entitled to it. Reach and
 *     Connect (slice 65 split the old Reach into the two), and since slice 59
 *     Sense.
 *   - `not_built` — **the software does not exist yet.** Flow.
 *   - `not_entitled` — built, but not on this tenant's plan; the repair is a
 *     conversation with sales, not with support.
 *
 * The distinction is the whole reason this shape exists. A disabled tile with a
 * lock on it says "you cannot have this", and a person who then buys it finds
 * nothing behind the lock. A tile that says "not built yet" is the truth and
 * costs nobody a support ticket. It is the same rule the rest of this slice
 * follows about open rates: never render an absence as a value.
 *
 * Slice 15 says the same words in a second place — the sidebar groups by pillar
 * — and it says
 * them by *importing* this vocabulary and {@link overviewPillar} rather than by
 * defining a lookalike. `./navigation.js` adds only what the sidebar needs on
 * top: which sections sit under a pillar, and the rule that only an `available`
 * pillar has a `sections` field at all. This file stays the single definition
 * of what may be said, so the Overview card and the sidebar group cannot come
 * to different conclusions about Flow.
 */
export const OVERVIEW_PILLAR_STATE_VALUES = ['available', 'not_built', 'not_entitled'] as const;
export const pillarStateSchema = z.enum(OVERVIEW_PILLAR_STATE_VALUES);
export type PillarState = z.infer<typeof pillarStateSchema>;

/**
 * Which pillars exist as software today.
 *
 * A constant map rather than a check scattered through the web app. `connect`
 * is true because slices 1–10 built email and WhatsApp campaigns, `reach`
 * because slices 40–52 built Meta ads (until slice 65 both were one pillar
 * called Reach), `sense` since slice 59 built Pressure, and `flow` since slices
 * 68–69 built the engine and the builder. (Until then this said `flow` was
 * false and that saying so was the state of the repository — superseded.)
 *
 * **Slice 15 made this two edits rather than one, and the superseded sentence
 * mattered enough to correct rather than leave standing.** This doc used to say
 * "the day Flow ships this file is the one edit". That is no longer true:
 * `./navigation.js` carries `PILLAR_SECTIONS`, the destinations that sit under
 * each pillar in the sidebar, and a pillar flipped to `true` here with no
 * sections there is a group with nothing in it. Flipping the flag alone would
 * have been enough while `not_built` only suppressed a card; it is not enough
 * now that it also decides whether anything is navigable.
 *
 * Both constants are edited together, and `navPillar` resolves a disagreement
 * between them in the fail-closed direction — no sections means not shipped,
 * whatever this flag says — so the worst case of forgetting one is a pillar
 * that stays honestly invisible rather than an empty group inviting clicks.
 */
export const PILLAR_IS_BUILT: Readonly<Record<Feature, boolean>> = {
  // Slice 65: Reach is Meta ads now (slices 40-52 built them); its sections are
  // `['ads']` in navigation.ts.
  reach: true,
  // Slice 65: Connect is the email and WhatsApp half of the old Reach — the
  // software slices 1-10 built — so it is built from the day it is named.
  connect: true,
  // Slice 69: the Flow builder and engine (slices 68–69) ship; flipped together
  // with `PILLAR_SECTIONS.flow = ['flows']` in navigation.ts.
  flow: true,
  // Slice 59: Sense ships its first screen, Pressure. Flipped together with
  // `PILLAR_SECTIONS.sense` in navigation.ts, as the paragraph above requires.
  sense: true,
};

export const overviewPillarSchema = z
  .object({
    feature: featureSchema,
    state: pillarStateSchema,
    /** True when the tenant's plan includes it, whether or not it exists. */
    entitled: z.boolean(),
  })
  .strict();
export type OverviewPillar = z.infer<typeof overviewPillarSchema>;

/**
 * Derives a pillar's state.
 *
 * `not_built` wins over `not_entitled`, always. Both can be true of Flow for a
 * tenant who did not buy it, and only one of them is worth telling them: being
 * sold something that does not exist is the worse outcome, and "not built yet"
 * is the answer that stays true after they buy it.
 */
export function overviewPillar(feature: Feature, entitled: boolean): OverviewPillar {
  const state: PillarState = !PILLAR_IS_BUILT[feature]
    ? 'not_built'
    : entitled
      ? 'available'
      : 'not_entitled';
  return { feature, state, entitled };
}

/* -------------------------------------------------------------------------- */
/* The response                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The stretch of time the counts on this screen describe.
 *
 * Carried explicitly rather than left to a caption somebody might change,
 * because every count in {@link overviewSendingHealthSchema} is meaningless
 * without it, and "3 bounces" over a day and over a month are different
 * sentences.
 */
export const overviewWindowSchema = z
  .object({
    days: z.number().int().min(1),
    /** Inclusive start. */
    since: isoDateTimeSchema,
    /** The moment the window was closed — equal to `generatedAt`. */
    until: isoDateTimeSchema,
  })
  .strict();
export type OverviewWindow = z.infer<typeof overviewWindowSchema>;

/**
 * `GET /overview`. Everything the Overview screen renders, in KPI order.
 *
 * Composed in the order SLICES fixes, and the order is load-bearing: the two
 * rates that can cost a tenant their AWS account come first, then the gap
 * between accepted and delivered, then whether this workspace can send at all,
 * then credits, then the suppression trend, then the last few campaigns.
 *
 * `generatedAt` is on the object because the whole page is a snapshot — one
 * read, one moment — and a screen that quietly refreshes half its numbers
 * invites the reader to compare two figures that came from different instants.
 *
 * `.strict()` is doing real work here. It means an engagement field cannot be
 * added by a well-meaning mapper and rendered before anybody reviews it: the
 * API's own response would fail to parse. There is still no open rate, no click
 * rate and no WhatsApp read count in this object. Slice 11 built the first two
 * and put them behind `engagementResponseSchema` in `./engagement.js` instead —
 * a separate read, because the panel beside them costs a live AWS call and this
 * response is defined by costing none. Adding a member here would repeal that
 * property in one line, which is why it is stated where somebody would try.
 *
 * `deliveryReports` is slice 19's member, and it does not repeal it: it is two
 * indexed reads of `delivery_events` and the stored SES row, in the same
 * transaction as everything else, and no provider is called to produce it. It
 * is the fact `noOutcomeReported` could only hedge about — whether a report has
 * ever reached Wend at all — and it belongs on the same response as the tiles
 * that say none has come back, so the two cannot come from different moments.
 * Nullable with a default, so a response from an API older than slice 19 reads
 * as "not known" and the screen falls back to the hedged sentence, rather than
 * as "never received".
 */
/**
 * "Pick up where you left off" — the campaigns that are NOT finished.
 *
 * WHY THIS IS NOT `recentCampaigns`
 * ---------------------------------------------------------------------------
 * `recentCampaigns` is a report on sends that have happened: it excludes
 * drafts, because a draft has sent nothing and can only contribute zeros. This
 * is the opposite question — what did somebody start and not finish — so it is
 * exactly the rows that one excludes, plus the scheduled ones that have not
 * fired yet.
 *
 * The two are drawn on the same page under different titles and must not be
 * confused for one another, which is why they are separate members rather than
 * one list with a flag.
 *
 * WHY IT IS NOT CALLED `openCampaigns`
 * ---------------------------------------------------------------------------
 * `verify-overview` asserts that no key on this response mentions open, click,
 * seen, engagement or a read receipt — the rule that keeps WhatsApp read
 * receipts and engagement figures off this screen entirely. "Open" here would
 * have meant unfinished, not opened, and a human reading the key would have
 * known that; the probe cannot, and should not have to. A blunt guard that
 * occasionally costs a better word is worth more than one with exceptions in
 * it, so the word changed rather than the guard.
 *
 * The card still says "4 open" on screen, where there is a title above it
 * saying what is being counted and no ambiguity about openings.
 *
 * WHY `reachable` IS NULLABLE, AND WHY THAT MATTERS MORE THAN IT LOOKS
 * ---------------------------------------------------------------------------
 * `campaigns.recipient_count` is 0 until the recipients are actually
 * materialised, which for a draft is usually never. Printing "0 reachable"
 * there would state, as a measured fact, that the campaign reaches nobody —
 * when what is true is that nobody has worked it out yet. Zero and
 * not-measured are different everywhere in this product, and a draft's reach is
 * the single easiest place on this page to get that wrong.
 *
 * So the API sends `null` for "not worked out yet" and a number only for a
 * count the database actually holds. There is no `?? 0` anywhere downstream.
 */
export const OVERVIEW_UNFINISHED_CAMPAIGN_LIMIT = 3;

export const overviewUnfinishedCampaignSchema = z
  .object({
    id: uuidSchema,
    name: z.string(),
    channel: channelSchema,
    /** Only ever `draft` or `scheduled` — the read filters to those two. */
    status: campaignStatusSchema,
    /** When it was last touched. The card says "edited" against this. */
    updatedAt: isoDateTimeSchema,
    /** Set only on a scheduled campaign. The card says "sends" against this. */
    scheduledAt: isoDateTimeSchema.nullable(),
    /** See the header: null means nobody has worked the reach out, not zero. */
    reachable: z.number().int().min(0).nullable(),
  })
  .strict();
export type OverviewUnfinishedCampaign = z.infer<typeof overviewUnfinishedCampaignSchema>;

export const overviewResponseSchema = z
  .object({
    generatedAt: isoDateTimeSchema,
    window: overviewWindowSchema,
    sending: overviewSendingHealthSchema,
    sendability: overviewSendabilitySchema,
    /** One entry per channel, zeros included. */
    credits: z.array(overviewChannelCreditsSchema),
    suppressions: overviewSuppressionSummarySchema,
    /** Most recent first, at most {@link OVERVIEW_RECENT_CAMPAIGN_LIMIT}. */
    recentCampaigns: z.array(overviewRecentCampaignSchema),
    /** Unfinished work, most recently touched first. See the schema's header. */
    unfinishedCampaigns: z.array(overviewUnfinishedCampaignSchema),
    /** One entry per {@link FEATURE_VALUES} member, including the unbuilt ones, in {@link PILLAR_ORDER}. */
    pillars: z.array(overviewPillarSchema),
    /** Whether delivery reports have ever reached Wend, per channel. See `./delivery-reports.js`. */
    deliveryReports: deliveryReportsSchema.nullable().default(null),
    /**
     * Projected delivery, opens and clicks over the messages nothing has been
     * reported for — `null` whenever a report of any kind has ever arrived.
     *
     * It does not repeal the paragraph above about engagement either, and the
     * distinction is worth being exact about: there is still no measured open
     * rate on this response and there never will be, because measuring one costs
     * a live AWS call this read is defined by not making. This member is not a
     * measurement. It is `awaitingEvents` multiplied by a stated assumption, it
     * is not assignable to any measured shape in the product, and it vanishes on
     * the first genuine notification. See `./projection.js` for what holds it
     * away from the halt gate and the ledger.
     */
    projection: projectedFiguresSchema.nullable().default(null),
  })
  .strict();
export type OverviewResponse = z.infer<typeof overviewResponseSchema>;
